import { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { GradeColorPicker } from './GradeColorPicker';
import { FALLBACK_GRADE_COLOR as FALLBACK_COLOR } from './gradeColors';
import { SettingsSaveBar } from './SettingsPanel';
import { useUpdateGradeScale } from './useUpdateGradeScale';

interface GradeScaleEntry {
  letter: string;
  minimumPercentage: number;
  gpaPoints: number;
  color?: string | null;
}


interface Props {
  schoolId?: string;
  entries?: ReadonlyArray<GradeScaleEntry>;
  onSaved?: () => void;
}

export default function GradeScaleEditor({ schoolId, entries, onSaved }: Props) {
  const [rows, setRows] = useState<GradeScaleEntry[]>([]);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updateGradeScale, { loading }] = useUpdateGradeScale();

  useEffect(() => {
    setRows([...(entries ?? [])].sort((a, b) => b.minimumPercentage - a.minimumPercentage));
    setDirty(false);
    setError(null);
  }, [entries]);

  const updateRow = (index: number, patch: Partial<GradeScaleEntry>) => {
    setRows((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, ...patch } : row));
    setDirty(true);
  };

  const addRow = () => {
    setRows((current) => [
      ...current,
      {
        letter: '',
        minimumPercentage: 0,
        gpaPoints: 0,
        // A new band starts as the band above it rather than as a neutral gray:
        // it is being added at the bottom of the scale, and matching its
        // neighbour is closer to right than starting from nothing.
        color: current[current.length - 1]?.color || FALLBACK_COLOR,
      },
    ]);
    setDirty(true);
  };

  const removeRow = (index: number) => {
    setRows((current) => current.filter((_, rowIndex) => rowIndex !== index));
    setDirty(true);
  };

  const save = async () => {
    if (!schoolId) return;
    setError(null);

    try {
      const result = await updateGradeScale({
        variables: {
          input: {
            schoolId,
            entries: rows.map((row) => ({
              letter: row.letter.trim(),
              minimumPercentage: Number(row.minimumPercentage),
              gpaPoints: Number(row.gpaPoints),
              color: row.color || null,
            })),
          },
        },
      });

      const errors = result.data?.updateGradeScale?.userErrors;
      if (errors?.length) {
        setError(errors.map((item) => item.message).join(', '));
        return;
      }

      setDirty(false);
      onSaved?.();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Could not save the grade scale');
    }
  };

  return (
    <>
      <p className="mb-4 max-w-[62ch] text-sm text-muted-foreground">
        Thresholds are inclusive and read from the highest down, so a grade takes the first
        letter it clears. The color is the one families see on that grade.
      </p>

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full min-w-[34rem]">
          <thead className="bg-muted/50">
            <tr>
              <th className="p-3 text-left text-sm font-medium">Letter</th>
              <th className="p-3 text-left text-sm font-medium">Minimum %</th>
              <th className="p-3 text-left text-sm font-medium">GPA points</th>
              <th className="p-3 text-left text-sm font-medium">Color</th>
              <th className="w-12" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr className="border-t" key={index}>
                <td className="p-2">
                  <Input
                    aria-label={`Letter ${index + 1}`}
                    value={row.letter}
                    onChange={(event) => updateRow(index, { letter: event.target.value })}
                    placeholder="A"
                  />
                </td>
                <td className="p-2">
                  <Input
                    aria-label={`Minimum percentage ${index + 1}`}
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    className="tabular-nums"
                    value={row.minimumPercentage}
                    onChange={(event) => updateRow(index, { minimumPercentage: Number(event.target.value) })}
                  />
                </td>
                <td className="p-2">
                  <Input
                    aria-label={`GPA points ${index + 1}`}
                    type="number"
                    min="0"
                    max="10"
                    step="0.01"
                    className="tabular-nums"
                    value={row.gpaPoints}
                    onChange={(event) => updateRow(index, { gpaPoints: Number(event.target.value) })}
                  />
                </td>
                <td className="p-2">
                  <div className="flex items-center gap-2">
                    <GradeColorPicker
                      label={row.letter.trim() || `row ${index + 1}`}
                      value={row.color}
                      onChange={(hex) => updateRow(index, { color: hex })}
                    />
                    <span
                      className="text-sm font-medium"
                      style={{ color: row.color || FALLBACK_COLOR }}
                    >
                      {row.letter.trim() || '—'}
                    </span>
                  </div>
                </td>
                  <td className="p-2">
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeRow(index)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
      </div>

      <div className="mt-3">
        <Button type="button" variant="outline" onClick={addRow} className="gap-2">
          <Plus className="h-4 w-4" />
          Add grade
        </Button>
      </div>

      <SettingsSaveBar
        dirty={dirty && rows.length > 0}
        saving={loading}
        error={error}
        onSave={save}
      />
    </>
  );
}
